import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import require$$0, { createContext, useRef, useMemo, useEffect, useContext, useCallback, useState } from "react";
const defaultTheme = {
  colors: {
    primary: "#10B981",
    accent: "#064E3B",
    background: "#ffffff",
    surface: "#f9fafb",
    text: "#111827",
    textLight: "#6b7280",
    textInverse: "#ffffff",
    border: "#e5e7eb",
    success: "#22c55e",
    error: "#ef4444",
    warning: "#f59e0b"
  },
  fonts: {
    family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    sizeXs: "0.75rem",
    sizeSm: "0.875rem",
    sizeBase: "1rem",
    sizeMd: "1.125rem",
    sizeLg: "1.25rem",
    sizeXl: "1.5rem",
    weightNormal: 400,
    weightMedium: 500,
    weightSemibold: 600,
    weightBold: 700
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
    xxl: "3rem"
  },
  borderRadius: {
    sm: "0.25rem",
    md: "0.5rem",
    lg: "0.75rem",
    xl: "1rem",
    full: "9999px"
  },
  shadows: {
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
  },
  animations: {
    fast: "150ms",
    normal: "300ms",
    slow: "500ms",
    easeOut: "cubic-bezier(0, 0, 0.2, 1)",
    easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    bounce: "cubic-bezier(0.68, -0.55, 0.265, 1.55)"
  },
  zIndex: {
    base: 1,
    dropdown: 1e3,
    sticky: 1100,
    modal: 1200,
    tooltip: 1300
  }
};
const spaTheme = {
  ...defaultTheme,
  colors: {
    ...defaultTheme.colors,
    primary: "#8b5cf6",
    accent: "#4c1d95"
  }
};
const ThemeContext = createContext(null);
function mergeTheme(base, overrides) {
  if (!overrides) {
    return base;
  }
  return {
    colors: { ...base.colors, ...overrides.colors },
    fonts: { ...base.fonts, ...overrides.fonts },
    spacing: { ...base.spacing, ...overrides.spacing },
    borderRadius: { ...base.borderRadius, ...overrides.borderRadius },
    shadows: { ...base.shadows, ...overrides.shadows },
    animations: { ...base.animations, ...overrides.animations },
    zIndex: { ...base.zIndex, ...overrides.zIndex }
  };
}
function generateCssVariables(theme) {
  return `
    --ck-color-primary: ${theme.colors.primary};
    --ck-color-accent: ${theme.colors.accent};
    --ck-color-background: ${theme.colors.background};
    --ck-color-surface: ${theme.colors.surface};
    --ck-color-text: ${theme.colors.text};
    --ck-color-text-light: ${theme.colors.textLight};
    --ck-color-text-inverse: ${theme.colors.textInverse};
    --ck-color-border: ${theme.colors.border};
    --ck-color-success: ${theme.colors.success};
    --ck-color-error: ${theme.colors.error};
    --ck-color-warning: ${theme.colors.warning};

    --ck-font-family: ${theme.fonts.family};
    --ck-font-size-xs: ${theme.fonts.sizeXs};
    --ck-font-size-sm: ${theme.fonts.sizeSm};
    --ck-font-size-base: ${theme.fonts.sizeBase};
    --ck-font-size-md: ${theme.fonts.sizeMd};
    --ck-font-size-lg: ${theme.fonts.sizeLg};
    --ck-font-size-xl: ${theme.fonts.sizeXl};
    --ck-font-weight-normal: ${theme.fonts.weightNormal};
    --ck-font-weight-medium: ${theme.fonts.weightMedium};
    --ck-font-weight-semibold: ${theme.fonts.weightSemibold};
    --ck-font-weight-bold: ${theme.fonts.weightBold};

    --ck-spacing-xs: ${theme.spacing.xs};
    --ck-spacing-sm: ${theme.spacing.sm};
    --ck-spacing-md: ${theme.spacing.md};
    --ck-spacing-lg: ${theme.spacing.lg};
    --ck-spacing-xl: ${theme.spacing.xl};
    --ck-spacing-xxl: ${theme.spacing.xxl};

    --ck-radius-sm: ${theme.borderRadius.sm};
    --ck-radius-md: ${theme.borderRadius.md};
    --ck-radius-lg: ${theme.borderRadius.lg};
    --ck-radius-xl: ${theme.borderRadius.xl};
    --ck-radius-full: ${theme.borderRadius.full};

    --ck-shadow-sm: ${theme.shadows.sm};
    --ck-shadow-md: ${theme.shadows.md};
    --ck-shadow-lg: ${theme.shadows.lg};
    --ck-shadow-xl: ${theme.shadows.xl};

    --ck-animation-fast: ${theme.animations.fast};
    --ck-animation-normal: ${theme.animations.normal};
    --ck-animation-slow: ${theme.animations.slow};
    --ck-animation-ease-out: ${theme.animations.easeOut};
    --ck-animation-ease-in-out: ${theme.animations.easeInOut};
    --ck-animation-bounce: ${theme.animations.bounce};

    --ck-z-base: ${theme.zIndex.base};
    --ck-z-dropdown: ${theme.zIndex.dropdown};
    --ck-z-sticky: ${theme.zIndex.sticky};
    --ck-z-modal: ${theme.zIndex.modal};
    --ck-z-tooltip: ${theme.zIndex.tooltip};
  `;
}
const KEYFRAMES = `
  @keyframes ck-pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.05); opacity: 0.8; }
  }

  @keyframes ck-bounce-in {
    0% { transform: scale(0); opacity: 0; }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); opacity: 1; }
  }

  @keyframes ck-slide-up {
    from { transform: translateY(100%); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  @keyframes ck-slide-down {
    from { transform: translateY(0); opacity: 1; }
    to { transform: translateY(100%); opacity: 0; }
  }

  @keyframes ck-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes ck-typing-dot {
    0%, 60%, 100% { transform: translateY(0); }
    30% { transform: translateY(-4px); }
  }
`;
function ThemeProvider({
  children,
  venueType = "restaurant",
  overrides
}) {
  const styleRef = useRef(null);
  const baseTheme = venueType === "spa" ? spaTheme : defaultTheme;
  const theme = useMemo(
    () => mergeTheme(baseTheme, overrides),
    [baseTheme, overrides]
  );
  useEffect(() => {
    if (!styleRef.current) {
      styleRef.current = document.createElement("style");
      styleRef.current.setAttribute("data-chekmenu-concierge", "true");
      document.head.appendChild(styleRef.current);
    }
    const cssVariables = generateCssVariables(theme);
    styleRef.current.textContent = `
      :root {
        ${cssVariables}
      }

      .chekmenu-concierge-root {
        ${cssVariables}
      }

      ${KEYFRAMES}
    `;
    return () => {
      if (styleRef.current && styleRef.current.parentNode) {
        styleRef.current.parentNode.removeChild(styleRef.current);
        styleRef.current = null;
      }
    };
  }, [theme]);
  const value = useMemo(() => ({ theme }), [theme]);
  return /* @__PURE__ */ jsx(ThemeContext.Provider, { value, children });
}
function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context.theme;
}
const __vite_import_meta_env__$1 = {};
const createStoreImpl = (createState) => {
  let state;
  const listeners = /* @__PURE__ */ new Set();
  const setState = (partial, replace) => {
    const nextState = typeof partial === "function" ? partial(state) : partial;
    if (!Object.is(nextState, state)) {
      const previousState = state;
      state = (replace != null ? replace : typeof nextState !== "object" || nextState === null) ? nextState : Object.assign({}, state, nextState);
      listeners.forEach((listener) => listener(state, previousState));
    }
  };
  const getState = () => state;
  const getInitialState = () => initialState2;
  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };
  const destroy = () => {
    if ((__vite_import_meta_env__$1 ? "production" : void 0) !== "production") {
      console.warn(
        "[DEPRECATED] The `destroy` method will be unsupported in a future version. Instead use unsubscribe function returned by subscribe. Everything will be garbage-collected if store is garbage-collected."
      );
    }
    listeners.clear();
  };
  const api = { setState, getState, getInitialState, subscribe, destroy };
  const initialState2 = state = createState(setState, getState, api);
  return api;
};
const createStore = (createState) => createState ? createStoreImpl(createState) : createStoreImpl;
function getDefaultExportFromCjs(x) {
  return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, "default") ? x["default"] : x;
}
var withSelector = { exports: {} };
var withSelector_production = {};
var shim = { exports: {} };
var useSyncExternalStoreShim_production = {};
/**
 * @license React
 * use-sync-external-store-shim.production.js
 *
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var hasRequiredUseSyncExternalStoreShim_production;
function requireUseSyncExternalStoreShim_production() {
  if (hasRequiredUseSyncExternalStoreShim_production) return useSyncExternalStoreShim_production;
  hasRequiredUseSyncExternalStoreShim_production = 1;
  var React = require$$0;
  function is(x, y) {
    return x === y && (0 !== x || 1 / x === 1 / y) || x !== x && y !== y;
  }
  var objectIs = "function" === typeof Object.is ? Object.is : is, useState2 = React.useState, useEffect2 = React.useEffect, useLayoutEffect = React.useLayoutEffect, useDebugValue2 = React.useDebugValue;
  function useSyncExternalStore$2(subscribe, getSnapshot) {
    var value = getSnapshot(), _useState = useState2({ inst: { value, getSnapshot } }), inst = _useState[0].inst, forceUpdate = _useState[1];
    useLayoutEffect(
      function() {
        inst.value = value;
        inst.getSnapshot = getSnapshot;
        checkIfSnapshotChanged(inst) && forceUpdate({ inst });
      },
      [subscribe, value, getSnapshot]
    );
    useEffect2(
      function() {
        checkIfSnapshotChanged(inst) && forceUpdate({ inst });
        return subscribe(function() {
          checkIfSnapshotChanged(inst) && forceUpdate({ inst });
        });
      },
      [subscribe]
    );
    useDebugValue2(value);
    return value;
  }
  function checkIfSnapshotChanged(inst) {
    var latestGetSnapshot = inst.getSnapshot;
    inst = inst.value;
    try {
      var nextValue = latestGetSnapshot();
      return !objectIs(inst, nextValue);
    } catch (error) {
      return true;
    }
  }
  function useSyncExternalStore$1(subscribe, getSnapshot) {
    return getSnapshot();
  }
  var shim2 = "undefined" === typeof window || "undefined" === typeof window.document || "undefined" === typeof window.document.createElement ? useSyncExternalStore$1 : useSyncExternalStore$2;
  useSyncExternalStoreShim_production.useSyncExternalStore = void 0 !== React.useSyncExternalStore ? React.useSyncExternalStore : shim2;
  return useSyncExternalStoreShim_production;
}
var useSyncExternalStoreShim_development = {};
/**
 * @license React
 * use-sync-external-store-shim.development.js
 *
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var hasRequiredUseSyncExternalStoreShim_development;
function requireUseSyncExternalStoreShim_development() {
  if (hasRequiredUseSyncExternalStoreShim_development) return useSyncExternalStoreShim_development;
  hasRequiredUseSyncExternalStoreShim_development = 1;
  "production" !== process.env.NODE_ENV && function() {
    function is(x, y) {
      return x === y && (0 !== x || 1 / x === 1 / y) || x !== x && y !== y;
    }
    function useSyncExternalStore$2(subscribe, getSnapshot) {
      didWarnOld18Alpha || void 0 === React.startTransition || (didWarnOld18Alpha = true, console.error(
        "You are using an outdated, pre-release alpha of React 18 that does not support useSyncExternalStore. The use-sync-external-store shim will not work correctly. Upgrade to a newer pre-release."
      ));
      var value = getSnapshot();
      if (!didWarnUncachedGetSnapshot) {
        var cachedValue = getSnapshot();
        objectIs(value, cachedValue) || (console.error(
          "The result of getSnapshot should be cached to avoid an infinite loop"
        ), didWarnUncachedGetSnapshot = true);
      }
      cachedValue = useState2({
        inst: { value, getSnapshot }
      });
      var inst = cachedValue[0].inst, forceUpdate = cachedValue[1];
      useLayoutEffect(
        function() {
          inst.value = value;
          inst.getSnapshot = getSnapshot;
          checkIfSnapshotChanged(inst) && forceUpdate({ inst });
        },
        [subscribe, value, getSnapshot]
      );
      useEffect2(
        function() {
          checkIfSnapshotChanged(inst) && forceUpdate({ inst });
          return subscribe(function() {
            checkIfSnapshotChanged(inst) && forceUpdate({ inst });
          });
        },
        [subscribe]
      );
      useDebugValue2(value);
      return value;
    }
    function checkIfSnapshotChanged(inst) {
      var latestGetSnapshot = inst.getSnapshot;
      inst = inst.value;
      try {
        var nextValue = latestGetSnapshot();
        return !objectIs(inst, nextValue);
      } catch (error) {
        return true;
      }
    }
    function useSyncExternalStore$1(subscribe, getSnapshot) {
      return getSnapshot();
    }
    "undefined" !== typeof __REACT_DEVTOOLS_GLOBAL_HOOK__ && "function" === typeof __REACT_DEVTOOLS_GLOBAL_HOOK__.registerInternalModuleStart && __REACT_DEVTOOLS_GLOBAL_HOOK__.registerInternalModuleStart(Error());
    var React = require$$0, objectIs = "function" === typeof Object.is ? Object.is : is, useState2 = React.useState, useEffect2 = React.useEffect, useLayoutEffect = React.useLayoutEffect, useDebugValue2 = React.useDebugValue, didWarnOld18Alpha = false, didWarnUncachedGetSnapshot = false, shim2 = "undefined" === typeof window || "undefined" === typeof window.document || "undefined" === typeof window.document.createElement ? useSyncExternalStore$1 : useSyncExternalStore$2;
    useSyncExternalStoreShim_development.useSyncExternalStore = void 0 !== React.useSyncExternalStore ? React.useSyncExternalStore : shim2;
    "undefined" !== typeof __REACT_DEVTOOLS_GLOBAL_HOOK__ && "function" === typeof __REACT_DEVTOOLS_GLOBAL_HOOK__.registerInternalModuleStop && __REACT_DEVTOOLS_GLOBAL_HOOK__.registerInternalModuleStop(Error());
  }();
  return useSyncExternalStoreShim_development;
}
var hasRequiredShim;
function requireShim() {
  if (hasRequiredShim) return shim.exports;
  hasRequiredShim = 1;
  if (process.env.NODE_ENV === "production") {
    shim.exports = requireUseSyncExternalStoreShim_production();
  } else {
    shim.exports = requireUseSyncExternalStoreShim_development();
  }
  return shim.exports;
}
/**
 * @license React
 * use-sync-external-store-shim/with-selector.production.js
 *
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var hasRequiredWithSelector_production;
function requireWithSelector_production() {
  if (hasRequiredWithSelector_production) return withSelector_production;
  hasRequiredWithSelector_production = 1;
  var React = require$$0, shim2 = requireShim();
  function is(x, y) {
    return x === y && (0 !== x || 1 / x === 1 / y) || x !== x && y !== y;
  }
  var objectIs = "function" === typeof Object.is ? Object.is : is, useSyncExternalStore = shim2.useSyncExternalStore, useRef2 = React.useRef, useEffect2 = React.useEffect, useMemo2 = React.useMemo, useDebugValue2 = React.useDebugValue;
  withSelector_production.useSyncExternalStoreWithSelector = function(subscribe, getSnapshot, getServerSnapshot, selector, isEqual) {
    var instRef = useRef2(null);
    if (null === instRef.current) {
      var inst = { hasValue: false, value: null };
      instRef.current = inst;
    } else inst = instRef.current;
    instRef = useMemo2(
      function() {
        function memoizedSelector(nextSnapshot) {
          if (!hasMemo) {
            hasMemo = true;
            memoizedSnapshot = nextSnapshot;
            nextSnapshot = selector(nextSnapshot);
            if (void 0 !== isEqual && inst.hasValue) {
              var currentSelection = inst.value;
              if (isEqual(currentSelection, nextSnapshot))
                return memoizedSelection = currentSelection;
            }
            return memoizedSelection = nextSnapshot;
          }
          currentSelection = memoizedSelection;
          if (objectIs(memoizedSnapshot, nextSnapshot)) return currentSelection;
          var nextSelection = selector(nextSnapshot);
          if (void 0 !== isEqual && isEqual(currentSelection, nextSelection))
            return memoizedSnapshot = nextSnapshot, currentSelection;
          memoizedSnapshot = nextSnapshot;
          return memoizedSelection = nextSelection;
        }
        var hasMemo = false, memoizedSnapshot, memoizedSelection, maybeGetServerSnapshot = void 0 === getServerSnapshot ? null : getServerSnapshot;
        return [
          function() {
            return memoizedSelector(getSnapshot());
          },
          null === maybeGetServerSnapshot ? void 0 : function() {
            return memoizedSelector(maybeGetServerSnapshot());
          }
        ];
      },
      [getSnapshot, getServerSnapshot, selector, isEqual]
    );
    var value = useSyncExternalStore(subscribe, instRef[0], instRef[1]);
    useEffect2(
      function() {
        inst.hasValue = true;
        inst.value = value;
      },
      [value]
    );
    useDebugValue2(value);
    return value;
  };
  return withSelector_production;
}
var withSelector_development = {};
/**
 * @license React
 * use-sync-external-store-shim/with-selector.development.js
 *
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var hasRequiredWithSelector_development;
function requireWithSelector_development() {
  if (hasRequiredWithSelector_development) return withSelector_development;
  hasRequiredWithSelector_development = 1;
  "production" !== process.env.NODE_ENV && function() {
    function is(x, y) {
      return x === y && (0 !== x || 1 / x === 1 / y) || x !== x && y !== y;
    }
    "undefined" !== typeof __REACT_DEVTOOLS_GLOBAL_HOOK__ && "function" === typeof __REACT_DEVTOOLS_GLOBAL_HOOK__.registerInternalModuleStart && __REACT_DEVTOOLS_GLOBAL_HOOK__.registerInternalModuleStart(Error());
    var React = require$$0, shim2 = requireShim(), objectIs = "function" === typeof Object.is ? Object.is : is, useSyncExternalStore = shim2.useSyncExternalStore, useRef2 = React.useRef, useEffect2 = React.useEffect, useMemo2 = React.useMemo, useDebugValue2 = React.useDebugValue;
    withSelector_development.useSyncExternalStoreWithSelector = function(subscribe, getSnapshot, getServerSnapshot, selector, isEqual) {
      var instRef = useRef2(null);
      if (null === instRef.current) {
        var inst = { hasValue: false, value: null };
        instRef.current = inst;
      } else inst = instRef.current;
      instRef = useMemo2(
        function() {
          function memoizedSelector(nextSnapshot) {
            if (!hasMemo) {
              hasMemo = true;
              memoizedSnapshot = nextSnapshot;
              nextSnapshot = selector(nextSnapshot);
              if (void 0 !== isEqual && inst.hasValue) {
                var currentSelection = inst.value;
                if (isEqual(currentSelection, nextSnapshot))
                  return memoizedSelection = currentSelection;
              }
              return memoizedSelection = nextSnapshot;
            }
            currentSelection = memoizedSelection;
            if (objectIs(memoizedSnapshot, nextSnapshot))
              return currentSelection;
            var nextSelection = selector(nextSnapshot);
            if (void 0 !== isEqual && isEqual(currentSelection, nextSelection))
              return memoizedSnapshot = nextSnapshot, currentSelection;
            memoizedSnapshot = nextSnapshot;
            return memoizedSelection = nextSelection;
          }
          var hasMemo = false, memoizedSnapshot, memoizedSelection, maybeGetServerSnapshot = void 0 === getServerSnapshot ? null : getServerSnapshot;
          return [
            function() {
              return memoizedSelector(getSnapshot());
            },
            null === maybeGetServerSnapshot ? void 0 : function() {
              return memoizedSelector(maybeGetServerSnapshot());
            }
          ];
        },
        [getSnapshot, getServerSnapshot, selector, isEqual]
      );
      var value = useSyncExternalStore(subscribe, instRef[0], instRef[1]);
      useEffect2(
        function() {
          inst.hasValue = true;
          inst.value = value;
        },
        [value]
      );
      useDebugValue2(value);
      return value;
    };
    "undefined" !== typeof __REACT_DEVTOOLS_GLOBAL_HOOK__ && "function" === typeof __REACT_DEVTOOLS_GLOBAL_HOOK__.registerInternalModuleStop && __REACT_DEVTOOLS_GLOBAL_HOOK__.registerInternalModuleStop(Error());
  }();
  return withSelector_development;
}
if (process.env.NODE_ENV === "production") {
  withSelector.exports = requireWithSelector_production();
} else {
  withSelector.exports = requireWithSelector_development();
}
var withSelectorExports = withSelector.exports;
const useSyncExternalStoreExports = /* @__PURE__ */ getDefaultExportFromCjs(withSelectorExports);
const __vite_import_meta_env__ = {};
const { useDebugValue } = require$$0;
const { useSyncExternalStoreWithSelector } = useSyncExternalStoreExports;
let didWarnAboutEqualityFn = false;
const identity = (arg) => arg;
function useStore(api, selector = identity, equalityFn) {
  if ((__vite_import_meta_env__ ? "production" : void 0) !== "production" && equalityFn && !didWarnAboutEqualityFn) {
    console.warn(
      "[DEPRECATED] Use `createWithEqualityFn` instead of `create` or use `useStoreWithEqualityFn` instead of `useStore`. They can be imported from 'zustand/traditional'. https://github.com/pmndrs/zustand/discussions/1937"
    );
    didWarnAboutEqualityFn = true;
  }
  const slice = useSyncExternalStoreWithSelector(
    api.subscribe,
    api.getState,
    api.getServerState || api.getInitialState,
    selector,
    equalityFn
  );
  useDebugValue(slice);
  return slice;
}
const createImpl = (createState) => {
  if ((__vite_import_meta_env__ ? "production" : void 0) !== "production" && typeof createState !== "function") {
    console.warn(
      "[DEPRECATED] Passing a vanilla store will be unsupported in a future version. Instead use `import { useStore } from 'zustand'`."
    );
  }
  const api = typeof createState === "function" ? createStore(createState) : createState;
  const useBoundStore = (selector, equalityFn) => useStore(api, selector, equalityFn);
  Object.assign(useBoundStore, api);
  return useBoundStore;
};
const create = (createState) => createState ? createImpl(createState) : createImpl;
class ApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = "ApiError";
  }
}
const DEFAULT_API_BASE_URL = "https://api.chekmenu.com";
function createConciergeApi(apiKey, baseUrl = "") {
  const resolvedBaseUrl = baseUrl && !baseUrl.includes("localhost") ? baseUrl : DEFAULT_API_BASE_URL;
  const headers = {
    "Content-Type": "application/json",
    "X-ChekMenu-API-Key": apiKey
  };
  async function request(endpoint, options) {
    const url = `${resolvedBaseUrl}${endpoint}`;
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      });
      if (!response.ok) {
        const errorBody = await response.text();
        let message = `HTTP ${response.status}: ${response.statusText}`;
        let code;
        try {
          const errorJson = JSON.parse(errorBody);
          message = errorJson.message || message;
          code = errorJson.code;
        } catch {
        }
        throw new ApiError(message, response.status, code);
      }
      return response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        error instanceof Error ? error.message : "Network request failed",
        0,
        "NETWORK_ERROR"
      );
    }
  }
  return {
    /**
     * Select/assign a concierge for the venue
     */
    async selectConcierge(payload) {
      return request("/concierge/select", {
        method: "POST",
        body: JSON.stringify(payload)
      });
    },
    /**
     * Send a chat message to the concierge
     */
    async sendMessage(payload) {
      const response = await request("/concierge/chat", {
        method: "POST",
        body: JSON.stringify(payload)
      });
      return {
        ...response,
        messages: response.recommendedItems || response.messages
      };
    }
  };
}
const STORAGE_KEYS = {
  WELCOME_SHOWN: "chekmenu_concierge_welcome_shown",
  DEVICE_ID: "chekmenu_device_id",
  SESSION_ID: "chekmenu_session_id"
};
function generateCanvasFingerprint() {
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas 2D context not available");
    }
    canvas.width = 200;
    canvas.height = 50;
    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillStyle = "#f60";
    ctx.fillRect(0, 0, 200, 50);
    ctx.fillStyle = "#069";
    ctx.fillText("ChekMenu Device Fingerprint", 2, 2);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("ChekMenu Device Fingerprint", 4, 4);
    const dataUrl = canvas.toDataURL();
    return btoa(dataUrl).slice(0, 32);
  } catch (err) {
    console.warn("[Fingerprint] Canvas fingerprinting failed:", err);
    return generateRandomId();
  }
}
function generateRandomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : r & 3 | 8;
    return v.toString(16);
  });
}
function getDeviceId() {
  try {
    const storedId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
    if (storedId) {
      return storedId;
    }
  } catch {
  }
  const newId = generateCanvasFingerprint();
  try {
    localStorage.setItem(STORAGE_KEYS.DEVICE_ID, newId);
  } catch {
  }
  return newId;
}
function generateSessionId() {
  const timestamp = Date.now();
  const random = Math.random().toString(36).slice(2, 11);
  return `session_${timestamp}_${random}`;
}
function getSessionId() {
  try {
    const storedId = sessionStorage.getItem(STORAGE_KEYS.SESSION_ID);
    if (storedId) {
      return storedId;
    }
  } catch {
  }
  const newId = generateSessionId();
  try {
    sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, newId);
  } catch {
  }
  return newId;
}
const DEFAULT_CONCIERGE_CONFIG = {
  name: "Assistant",
  venue: "Restaurant",
  venueType: "restaurant",
  avatar: "👩‍🍳",
  primaryColor: "#10B981",
  accentColor: "#064E3B"
};
const initialState = {
  venueId: "",
  apiKey: "",
  baseUrl: "",
  deviceId: "",
  sessionId: "",
  conciergeId: null,
  config: DEFAULT_CONCIERGE_CONFIG,
  isOpen: false,
  showWelcomePopup: false,
  isTyping: false,
  isInitialized: false,
  isInitializing: false,
  messages: [],
  messageIdCounter: 0,
  error: null
};
const useConciergeStore = create((set, get) => ({
  ...initialState,
  /**
   * Initialize the concierge (call /concierge/select)
   */
  initialize: async (venueId, apiKey, baseUrl = "") => {
    const { isInitialized, isInitializing } = get();
    if (isInitialized || isInitializing) {
      return;
    }
    set({
      venueId,
      apiKey,
      baseUrl,
      isInitializing: true,
      error: null
    });
    const deviceId = getDeviceId();
    const sessionId = getSessionId();
    set({ deviceId, sessionId });
    try {
      const api = createConciergeApi(apiKey, baseUrl);
      const response = await api.selectConcierge({
        venueId,
        deviceId
      });
      set({
        conciergeId: response.conciergeId,
        config: response.config,
        isInitialized: true,
        isInitializing: false
      });
    } catch (error) {
      console.error("[ConciergeStore] Failed to initialize:", error);
      set({
        config: DEFAULT_CONCIERGE_CONFIG,
        conciergeId: "default",
        isInitialized: true,
        isInitializing: false,
        error: error instanceof ApiError ? error.message : "Failed to initialize concierge"
      });
    }
  },
  /**
   * Set chat open state
   */
  setOpen: (open) => {
    set({ isOpen: open });
  },
  /**
   * Open chat and add greeting if needed
   */
  openChat: () => {
    const { messages, config, isInitialized } = get();
    set({ isOpen: true, showWelcomePopup: false });
    if (messages.length === 0 && isInitialized) {
      const greeting = getWelcomeMessage(config);
      const suggestions = getSuggestions(config.venueType);
      setTimeout(() => {
        get().addMessage(greeting, false, suggestions);
      }, 300);
    }
  },
  /**
   * Close chat
   */
  closeChat: () => {
    set({ isOpen: false });
  },
  /**
   * Show/hide welcome popup
   */
  setShowWelcomePopup: (show) => {
    set({ showWelcomePopup: show });
  },
  /**
   * Add a message to the chat
   */
  addMessage: (text, isUser, suggestions, menuItems) => {
    const { messageIdCounter, messages } = get();
    const newId = messageIdCounter + 1;
    const message = {
      id: newId.toString(),
      text,
      isUser,
      timestamp: /* @__PURE__ */ new Date(),
      suggestions,
      menuItems: menuItems == null ? void 0 : menuItems.map((item) => ({
        ...item,
        currentSlide: 0
      }))
    };
    set({
      messages: [...messages, message],
      messageIdCounter: newId
    });
  },
  /**
   * Send a user message
   */
  sendMessage: async (text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const { apiKey, baseUrl, deviceId, venueId, sessionId, conciergeId } = get();
    if (!conciergeId) {
      console.error("[ConciergeStore] No concierge ID available");
      get().addMessage(
        "I'm not ready yet. Please wait a moment and try again.",
        false
      );
      return;
    }
    get().addMessage(trimmed, true);
    set({ isTyping: true, error: null });
    try {
      const api = createConciergeApi(apiKey, baseUrl);
      const response = await api.sendMessage({
        message: trimmed,
        sessionId,
        deviceId,
        venueId,
        conciergeId
      });
      if (response.sessionId) {
        set({ sessionId: response.sessionId });
      }
      get().addMessage(
        response.text,
        false,
        response.suggestions,
        response.messages
      );
    } catch (error) {
      console.error("[ConciergeStore] Failed to send message:", error);
      get().addMessage(
        "I'm having trouble right now. Please try again in a moment.",
        false,
        ["Try again", "Show me popular items"]
      );
      set({
        error: error instanceof ApiError ? error.message : "Failed to send message"
      });
    } finally {
      set({ isTyping: false });
    }
  },
  /**
   * Send a suggestion as a message
   */
  sendSuggestion: async (suggestion) => {
    await get().sendMessage(suggestion);
  },
  /**
   * Update carousel slide for a menu item
   */
  updateCarouselSlide: (messageId, itemIndex, slideIndex) => {
    var _a;
    const { messages } = get();
    const messageIdx = messages.findIndex((m) => m.id === messageId);
    if (messageIdx === -1) return;
    const message = messages[messageIdx];
    if (!((_a = message.menuItems) == null ? void 0 : _a[itemIndex])) return;
    const updatedMessages = [...messages];
    updatedMessages[messageIdx] = {
      ...message,
      menuItems: message.menuItems.map(
        (item, idx) => idx === itemIndex ? { ...item, currentSlide: slideIndex } : item
      )
    };
    set({ messages: updatedMessages });
  },
  /**
   * Clear all messages
   */
  clearMessages: () => {
    set({
      messages: [],
      messageIdCounter: 0
    });
  },
  /**
   * Clear error
   */
  clearError: () => {
    set({ error: null });
  },
  /**
   * Reset store to initial state
   */
  reset: () => {
    set(initialState);
  }
}));
function getWelcomeMessage(config) {
  const hour = (/* @__PURE__ */ new Date()).getHours();
  const baseName = `I'm ${config.name}, your personal concierge at ${config.venue}.`;
  if (config.venueType === "restaurant") {
    if (hour < 11) {
      return `Good morning! ${baseName} Looking for a perfect breakfast or something light to start your day?`;
    } else if (hour < 17) {
      return `Good afternoon! ${baseName} Ready for lunch or exploring our signature dishes?`;
    } else {
      return `Good evening! ${baseName} Shall we find you something special for dinner tonight?`;
    }
  } else {
    if (hour < 11) {
      return `Good morning! ${baseName} Perfect time for a relaxing morning treatment or massage.`;
    } else if (hour < 17) {
      return `Good afternoon! ${baseName} How about a refreshing spa session to recharge your day?`;
    } else {
      return `Good evening! ${baseName} Unwind with an evening massage or soothing therapy tonight.`;
    }
  }
}
function getSuggestions(venueType) {
  const hour = (/* @__PURE__ */ new Date()).getHours();
  if (venueType === "restaurant") {
    if (hour < 11) {
      return [
        "What's good for breakfast?",
        "I'd like something light to start my day",
        "Show me coffee or juice options",
        "Any fresh pastries today?"
      ];
    } else if (hour < 17) {
      return [
        "What's popular for lunch right now?",
        "I'm looking for something quick",
        "Any chef's specials today?",
        "Recommend a drink with my meal"
      ];
    } else {
      return [
        "What's special for dinner tonight?",
        "Show me signature dishes",
        "Any wine pairings available?",
        "What's a good sharing option?"
      ];
    }
  } else {
    if (hour < 11) {
      return [
        "Recommend a morning ritual",
        "I'd like a refreshing facial",
        "What treatments help energize?",
        "Any quick 30-min options?"
      ];
    } else if (hour < 17) {
      return [
        "What treatments do you recommend today?",
        "I need a stress-relief massage",
        "Show me spa packages",
        "Any specials or seasonal offers?"
      ];
    } else {
      return [
        "Suggest a treatment to unwind",
        "I'd like a calming massage",
        "Any evening relaxation specials?",
        "Show me treatments for better sleep"
      ];
    }
  }
}
function getStorageKey(conciergeName) {
  return `${STORAGE_KEYS.WELCOME_SHOWN}_${conciergeName}`;
}
function wasWelcomeShown(conciergeName) {
  try {
    return sessionStorage.getItem(getStorageKey(conciergeName)) === "true";
  } catch {
    return false;
  }
}
function markWelcomeShown(conciergeName) {
  try {
    sessionStorage.setItem(getStorageKey(conciergeName), "true");
  } catch {
  }
}
function useWelcomePopup({
  delay = 2500,
  enabled = true
} = {}) {
  const showWelcomePopup = useConciergeStore((s) => s.showWelcomePopup);
  const setShowWelcomePopup = useConciergeStore((s) => s.setShowWelcomePopup);
  const isOpen = useConciergeStore((s) => s.isOpen);
  const isInitialized = useConciergeStore((s) => s.isInitialized);
  const config = useConciergeStore((s) => s.config);
  const openChatAction = useConciergeStore((s) => s.openChat);
  useEffect(() => {
    if (!enabled || !isInitialized || isOpen) return;
    const alreadyShown = wasWelcomeShown(config.name);
    if (alreadyShown) return;
    const timer = setTimeout(() => {
      if (!useConciergeStore.getState().isOpen) {
        setShowWelcomePopup(true);
        markWelcomeShown(config.name);
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [enabled, isInitialized, isOpen, config.name, delay, setShowWelcomePopup]);
  const dismissPopup = useCallback(() => {
    setShowWelcomePopup(false);
  }, [setShowWelcomePopup]);
  const openChat = useCallback(() => {
    setShowWelcomePopup(false);
    openChatAction();
  }, [setShowWelcomePopup, openChatAction]);
  return {
    showPopup: showWelcomePopup,
    dismissPopup,
    openChat
  };
}
function ChatIcon({
  size = 24,
  color = "currentColor",
  className
}) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: color,
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className,
      children: /* @__PURE__ */ jsx("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" })
    }
  );
}
function CloseIcon({
  size = 24,
  color = "currentColor",
  className
}) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: color,
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className,
      children: [
        /* @__PURE__ */ jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }),
        /* @__PURE__ */ jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })
      ]
    }
  );
}
function SendIcon({
  size = 24,
  color = "currentColor",
  className
}) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: color,
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className,
      children: [
        /* @__PURE__ */ jsx("line", { x1: "22", y1: "2", x2: "11", y2: "13" }),
        /* @__PURE__ */ jsx("polygon", { points: "22 2 15 22 11 13 2 9 22 2" })
      ]
    }
  );
}
function ChevronLeftIcon({
  size = 24,
  color = "currentColor",
  className
}) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: color,
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className,
      children: /* @__PURE__ */ jsx("polyline", { points: "15 18 9 12 15 6" })
    }
  );
}
function ChevronRightIcon({
  size = 24,
  color = "currentColor",
  className
}) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: color,
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className,
      children: /* @__PURE__ */ jsx("polyline", { points: "9 18 15 12 9 6" })
    }
  );
}
function StarIcon({
  size = 24,
  color = "currentColor",
  className
}) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: color,
      stroke: color,
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className,
      children: /* @__PURE__ */ jsx("polygon", { points: "12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" })
    }
  );
}
function ClockIcon({
  size = 24,
  color = "currentColor",
  className
}) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: color,
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className,
      children: [
        /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10" }),
        /* @__PURE__ */ jsx("polyline", { points: "12 6 12 12 16 14" })
      ]
    }
  );
}
function FireIcon({
  size = 24,
  color = "currentColor",
  className
}) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: color,
      stroke: "none",
      className,
      children: /* @__PURE__ */ jsx("path", { d: "M12 23c-4.42 0-8-3.58-8-8 0-3.31 2.01-6.67 5.05-9.67.56-.55 1.5-.14 1.5.65v2.92c0 1.18.96 2.14 2.14 2.14.59 0 1.14-.24 1.53-.63.39-.39.63-.94.63-1.53V4.58c0-.79.94-1.2 1.5-.65C19.99 7.33 22 10.69 22 14c0 4.42-3.58 8-8 8z" })
    }
  );
}
function WineIcon({
  size = 24,
  color = "currentColor",
  className
}) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: color,
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className,
      children: [
        /* @__PURE__ */ jsx("path", { d: "M8 22h8" }),
        /* @__PURE__ */ jsx("path", { d: "M12 11v11" }),
        /* @__PURE__ */ jsx("path", { d: "M17.5 2H6.5a.5.5 0 0 0-.5.5v5a6 6 0 0 0 12 0v-5a.5.5 0 0 0-.5-.5Z" })
      ]
    }
  );
}
function ChatButton({
  isOpen,
  onClick,
  position = "bottom-right",
  primaryColor
}) {
  const theme = useTheme();
  const color = primaryColor || theme.colors.primary;
  const styles = useMemo(
    () => ({
      button: {
        position: "fixed",
        bottom: "14px",
        [position === "bottom-right" ? "right" : "left"]: "14px",
        width: "60px",
        height: "60px",
        borderRadius: "50%",
        backgroundColor: color,
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: theme.shadows.lg,
        transition: `all ${theme.animations.normal} ${theme.animations.easeOut}`,
        zIndex: theme.zIndex.modal,
        outline: "none",
        // Prevent clipping and blur
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
        transform: "translateZ(0)"
      },
      buttonHover: {
        transform: "scale(1.05)",
        boxShadow: theme.shadows.xl
      },
      icon: {
        color: theme.colors.textInverse,
        transition: `transform ${theme.animations.normal} ${theme.animations.easeOut}`
      },
      pulse: {
        position: "absolute",
        top: "-4px",
        left: "-4px",
        width: "calc(100% + 8px)",
        height: "calc(100% + 8px)",
        borderRadius: "50%",
        backgroundColor: color,
        animation: "ck-pulse 2s infinite",
        opacity: 0.4,
        pointerEvents: "none"
      }
    }),
    [theme, color, position]
  );
  const [isHovered, setIsHovered] = require$$0.useState(false);
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      onClick,
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      style: {
        ...styles.button,
        ...isHovered ? styles.buttonHover : {}
      },
      "aria-label": isOpen ? "Close chat" : "Open chat",
      "aria-expanded": isOpen,
      children: [
        !isOpen && /* @__PURE__ */ jsx("span", { style: styles.pulse }),
        /* @__PURE__ */ jsx(
          "span",
          {
            style: {
              ...styles.icon,
              transform: isOpen ? "rotate(90deg)" : "rotate(0deg)"
            },
            children: isOpen ? /* @__PURE__ */ jsx(CloseIcon, { size: 28, color: theme.colors.textInverse }) : /* @__PURE__ */ jsx(ChatIcon, { size: 28, color: theme.colors.textInverse })
          }
        )
      ]
    }
  );
}
function useAutoScroll(messageCount) {
  const containerRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      lastScrollTopRef.current = containerRef.current.scrollTop;
    }
  }, []);
  const onScroll = useCallback((event) => {
    const container = event.currentTarget;
    lastScrollTopRef.current = container.scrollTop;
    const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50;
    shouldAutoScrollRef.current = isAtBottom;
  }, []);
  useEffect(() => {
    if (shouldAutoScrollRef.current) {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [messageCount, scrollToBottom]);
  return {
    containerRef,
    scrollToBottom,
    onScroll
  };
}
function TypingIndicator({ avatar = "👩‍🍳" }) {
  const theme = useTheme();
  const styles = useMemo(
    () => ({
      container: {
        display: "flex",
        alignItems: "flex-end",
        gap: theme.spacing.sm,
        marginBottom: theme.spacing.md
      },
      avatar: {
        fontSize: "1.5rem",
        lineHeight: 1,
        flexShrink: 0
      },
      bubble: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        display: "flex",
        alignItems: "center",
        gap: "4px"
      },
      dot: {
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        backgroundColor: theme.colors.textLight
      }
    }),
    [theme]
  );
  return /* @__PURE__ */ jsxs("div", { style: styles.container, children: [
    /* @__PURE__ */ jsx("span", { style: styles.avatar, children: avatar }),
    /* @__PURE__ */ jsx("div", { style: styles.bubble, children: [0, 1, 2].map((i) => /* @__PURE__ */ jsx(
      "span",
      {
        style: {
          ...styles.dot,
          animation: `ck-typing-dot 1.4s ease-in-out infinite`,
          animationDelay: `${i * 0.2}s`
        }
      },
      i
    )) })
  ] });
}
function SuggestionChips({
  suggestions,
  onSelect,
  primaryColor
}) {
  const theme = useTheme();
  const color = primaryColor || theme.colors.primary;
  const styles = useMemo(
    () => ({
      container: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing.sm,
        marginTop: theme.spacing.sm
      },
      chip: {
        padding: `${theme.spacing.xs} ${theme.spacing.md}`,
        backgroundColor: "transparent",
        border: `1px solid ${color}`,
        borderRadius: theme.borderRadius.full,
        color,
        fontSize: theme.fonts.sizeSm,
        cursor: "pointer",
        transition: `all ${theme.animations.fast}`,
        fontFamily: theme.fonts.family,
        whiteSpace: "nowrap"
      },
      chipHover: {
        backgroundColor: color,
        color: theme.colors.textInverse
      }
    }),
    [theme, color]
  );
  const [hoveredIndex, setHoveredIndex] = useState(null);
  if (!suggestions || suggestions.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsx("div", { style: styles.container, children: suggestions.map((suggestion, index) => /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick: () => onSelect(suggestion),
      onMouseEnter: () => setHoveredIndex(index),
      onMouseLeave: () => setHoveredIndex(null),
      style: {
        ...styles.chip,
        ...hoveredIndex === index ? styles.chipHover : {}
      },
      children: suggestion
    },
    index
  )) });
}
const AUTO_ADVANCE_INTERVAL = 4e3;
function useCarousel({
  messageId,
  itemIndex,
  totalSlides,
  enabled = true
}) {
  var _a, _b;
  const intervalRef = useRef();
  const updateCarouselSlide = useConciergeStore((s) => s.updateCarouselSlide);
  const messages = useConciergeStore((s) => s.messages);
  const message = messages.find((m) => m.id === messageId);
  const currentSlide = ((_b = (_a = message == null ? void 0 : message.menuItems) == null ? void 0 : _a[itemIndex]) == null ? void 0 : _b.currentSlide) ?? 0;
  const goToSlide = useCallback(
    (index) => {
      const normalizedIndex = (index % totalSlides + totalSlides) % totalSlides;
      updateCarouselSlide(messageId, itemIndex, normalizedIndex);
    },
    [messageId, itemIndex, totalSlides, updateCarouselSlide]
  );
  const nextSlide = useCallback(() => {
    goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);
  const prevSlide = useCallback(() => {
    goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);
  const startAutoAdvance = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      var _a2, _b2;
      const msg = useConciergeStore.getState().messages.find((m) => m.id === messageId);
      const slide = ((_b2 = (_a2 = msg == null ? void 0 : msg.menuItems) == null ? void 0 : _a2[itemIndex]) == null ? void 0 : _b2.currentSlide) ?? 0;
      const next = (slide + 1) % totalSlides;
      updateCarouselSlide(messageId, itemIndex, next);
    }, AUTO_ADVANCE_INTERVAL);
  }, [messageId, itemIndex, totalSlides, updateCarouselSlide]);
  const resetAutoAdvance = useCallback(() => {
    if (enabled && totalSlides > 1) {
      startAutoAdvance();
    }
  }, [enabled, totalSlides, startAutoAdvance]);
  const goToSlideWithReset = useCallback(
    (index) => {
      goToSlide(index);
      resetAutoAdvance();
    },
    [goToSlide, resetAutoAdvance]
  );
  useEffect(() => {
    if (enabled && totalSlides > 1) {
      startAutoAdvance();
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, totalSlides, startAutoAdvance]);
  return {
    currentSlide,
    goToSlide: goToSlideWithReset,
    nextSlide,
    prevSlide
  };
}
function MenuItemCard({
  item,
  messageId,
  itemIndex,
  primaryColor
}) {
  var _a;
  const theme = useTheme();
  const color = primaryColor || theme.colors.primary;
  const hasMultipleImages = item.itemImageLinks && item.itemImageLinks.length > 1;
  const totalSlides = ((_a = item.itemImageLinks) == null ? void 0 : _a.length) || 0;
  const { currentSlide, goToSlide, nextSlide, prevSlide } = useCarousel({
    messageId,
    itemIndex,
    totalSlides,
    enabled: hasMultipleImages
  });
  const [imageError, setImageError] = useState(false);
  const currentImageUrl = !imageError && item.itemImageLinks && item.itemImageLinks.length > 0 ? item.itemImageLinks[currentSlide] || item.itemImageLinks[0] : "/placeholder-food.jpg";
  const styles = useMemo(
    () => ({
      card: {
        backgroundColor: theme.colors.background,
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.shadows.md,
        overflow: "hidden",
        marginBottom: theme.spacing.md,
        fontFamily: theme.fonts.family
      },
      imageContainer: {
        position: "relative",
        width: "100%",
        paddingBottom: "60%",
        backgroundColor: theme.colors.surface,
        overflow: "hidden"
      },
      image: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        transition: `opacity ${theme.animations.normal}`
      },
      navButton: {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        width: "32px",
        height: "32px",
        borderRadius: "50%",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: theme.shadows.sm,
        transition: `opacity ${theme.animations.fast}`,
        zIndex: 2
      },
      dots: {
        position: "absolute",
        bottom: "8px",
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        gap: "6px",
        zIndex: 2
      },
      dot: {
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        border: "none",
        cursor: "pointer",
        transition: `all ${theme.animations.fast}`,
        padding: 0
      },
      badges: {
        position: "absolute",
        top: "8px",
        left: "8px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        zIndex: 2
      },
      badge: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 8px",
        borderRadius: theme.borderRadius.sm,
        fontSize: theme.fonts.sizeXs,
        fontWeight: theme.fonts.weightMedium
      },
      content: {
        padding: theme.spacing.md
      },
      header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: theme.spacing.xs
      },
      name: {
        margin: 0,
        fontSize: theme.fonts.sizeMd,
        fontWeight: theme.fonts.weightSemibold,
        color: theme.colors.text,
        flex: 1
      },
      price: {
        fontSize: theme.fonts.sizeMd,
        fontWeight: theme.fonts.weightBold,
        color,
        marginLeft: theme.spacing.sm
      },
      description: {
        margin: `${theme.spacing.xs} 0`,
        fontSize: theme.fonts.sizeSm,
        color: theme.colors.textLight,
        lineHeight: 1.4,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden"
      },
      meta: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing.md,
        marginTop: theme.spacing.sm,
        fontSize: theme.fonts.sizeXs,
        color: theme.colors.textLight
      },
      metaItem: {
        display: "flex",
        alignItems: "center",
        gap: "4px"
      },
      flair: {
        marginTop: theme.spacing.sm,
        padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
        backgroundColor: `${color}15`,
        borderRadius: theme.borderRadius.sm,
        fontSize: theme.fonts.sizeXs,
        color,
        fontWeight: theme.fonts.weightMedium
      },
      pairingsSection: {
        marginTop: theme.spacing.md,
        paddingTop: theme.spacing.md,
        borderTop: `1px solid ${theme.colors.border}`
      },
      pairingsTitle: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing.xs,
        fontSize: theme.fonts.sizeSm,
        fontWeight: theme.fonts.weightMedium,
        color: theme.colors.text,
        marginBottom: theme.spacing.sm
      },
      pairingsScroll: {
        display: "flex",
        gap: theme.spacing.sm,
        overflowX: "auto",
        paddingBottom: theme.spacing.xs,
        msOverflowStyle: "none",
        scrollbarWidth: "none"
      },
      pairingCard: {
        flexShrink: 0,
        width: "120px",
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        overflow: "hidden"
      },
      pairingImage: {
        width: "100%",
        height: "80px",
        objectFit: "cover",
        backgroundColor: theme.colors.border
      },
      pairingContent: {
        padding: theme.spacing.xs
      },
      pairingName: {
        fontSize: theme.fonts.sizeXs,
        fontWeight: theme.fonts.weightMedium,
        color: theme.colors.text,
        margin: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
      },
      pairingReason: {
        fontSize: "10px",
        color: theme.colors.textLight,
        margin: "2px 0 0",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }
    }),
    [theme, color]
  );
  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(price);
  };
  return /* @__PURE__ */ jsxs("div", { style: styles.card, children: [
    /* @__PURE__ */ jsxs("div", { style: styles.imageContainer, children: [
      /* @__PURE__ */ jsx(
        "img",
        {
          src: currentImageUrl,
          alt: item.name,
          style: styles.image,
          onError: () => setImageError(true)
        }
      ),
      /* @__PURE__ */ jsxs("div", { style: styles.badges, children: [
        item.chefSpecial && /* @__PURE__ */ jsxs(
          "span",
          {
            style: {
              ...styles.badge,
              backgroundColor: "#FEF3C7",
              color: "#92400E"
            },
            children: [
              /* @__PURE__ */ jsx(StarIcon, { size: 12, color: "#F59E0B" }),
              "Chef's Special"
            ]
          }
        ),
        item.bestSeller && /* @__PURE__ */ jsxs(
          "span",
          {
            style: {
              ...styles.badge,
              backgroundColor: "#FEE2E2",
              color: "#991B1B"
            },
            children: [
              /* @__PURE__ */ jsx(FireIcon, { size: 12, color: "#EF4444" }),
              "Best Seller"
            ]
          }
        )
      ] }),
      hasMultipleImages && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: prevSlide,
            style: { ...styles.navButton, left: "8px" },
            "aria-label": "Previous image",
            children: /* @__PURE__ */ jsx(ChevronLeftIcon, { size: 16, color: theme.colors.text })
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: nextSlide,
            style: { ...styles.navButton, right: "8px" },
            "aria-label": "Next image",
            children: /* @__PURE__ */ jsx(ChevronRightIcon, { size: 16, color: theme.colors.text })
          }
        )
      ] }),
      hasMultipleImages && /* @__PURE__ */ jsx("div", { style: styles.dots, children: item.itemImageLinks.map((_, index) => /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => goToSlide(index),
          style: {
            ...styles.dot,
            backgroundColor: index === currentSlide ? theme.colors.textInverse : "rgba(255, 255, 255, 0.5)"
          },
          "aria-label": `Go to slide ${index + 1}`
        },
        index
      )) })
    ] }),
    /* @__PURE__ */ jsxs("div", { style: styles.content, children: [
      /* @__PURE__ */ jsxs("div", { style: styles.header, children: [
        /* @__PURE__ */ jsx(
          "h4",
          {
            style: styles.name,
            dangerouslySetInnerHTML: { __html: item.name }
          }
        ),
        item.price > 0 && /* @__PURE__ */ jsx("span", { style: styles.price, children: formatPrice(item.price) })
      ] }),
      item.description && /* @__PURE__ */ jsx(
        "div",
        {
          style: styles.description,
          dangerouslySetInnerHTML: { __html: item.description }
        }
      ),
      /* @__PURE__ */ jsxs("div", { style: styles.meta, children: [
        item.prepTime && item.prepTime > 0 && /* @__PURE__ */ jsxs("span", { style: styles.metaItem, children: [
          /* @__PURE__ */ jsx(ClockIcon, { size: 12, color: theme.colors.textLight }),
          item.prepTime,
          " min"
        ] }),
        item.likesCount && item.likesCount > 0 && /* @__PURE__ */ jsxs("span", { style: styles.metaItem, children: [
          "❤️ ",
          item.likesCount
        ] })
      ] }),
      item.flairText && /* @__PURE__ */ jsx(
        "div",
        {
          style: styles.flair,
          dangerouslySetInnerHTML: { __html: item.flairText }
        }
      ),
      item.pairings && item.pairings.length > 0 && /* @__PURE__ */ jsxs("div", { style: styles.pairingsSection, children: [
        /* @__PURE__ */ jsxs("div", { style: styles.pairingsTitle, children: [
          /* @__PURE__ */ jsx(WineIcon, { size: 16, color }),
          "Perfect Pairings"
        ] }),
        /* @__PURE__ */ jsx("div", { style: styles.pairingsScroll, children: item.pairings.map((pairing) => /* @__PURE__ */ jsxs("div", { style: styles.pairingCard, children: [
          /* @__PURE__ */ jsx(
            "img",
            {
              src: pairing.imageThumbnail || "/placeholder-food.jpg",
              alt: pairing.name,
              style: styles.pairingImage
            }
          ),
          /* @__PURE__ */ jsxs("div", { style: styles.pairingContent, children: [
            /* @__PURE__ */ jsx(
              "p",
              {
                style: styles.pairingName,
                dangerouslySetInnerHTML: { __html: pairing.name }
              }
            ),
            pairing.reason && /* @__PURE__ */ jsx(
              "p",
              {
                style: styles.pairingReason,
                dangerouslySetInnerHTML: { __html: pairing.reason }
              }
            )
          ] })
        ] }, pairing.id)) })
      ] })
    ] })
  ] });
}
function ChatWindow({
  isOpen,
  onClose,
  config,
  messages,
  isTyping,
  onSendMessage,
  onSuggestionClick,
  position = "bottom-right"
}) {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);
  const primaryColor = config.primaryColor || theme.colors.primary;
  const { containerRef, onScroll } = useAutoScroll(messages.length);
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        var _a;
        (_a = inputRef.current) == null ? void 0 : _a.focus();
      }, 350);
    }
  }, [isOpen]);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      onSendMessage(inputValue.trim());
      setInputValue("");
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };
  const styles = useMemo(
    () => ({
      overlay: {
        position: "fixed",
        bottom: "90px",
        [position === "bottom-right" ? "right" : "left"]: "14px",
        width: "380px",
        maxWidth: "calc(100vw - 64px)",
        height: "600px",
        maxHeight: "calc(100vh - 160px)",
        backgroundColor: theme.colors.background,
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadows.xl,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: theme.zIndex.modal,
        fontFamily: theme.fonts.family,
        animation: isOpen ? `ck-slide-up ${theme.animations.normal} ${theme.animations.easeOut} forwards` : "none",
        // Prevent blurry text from transform animations
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
        transform: "translateZ(0)",
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale"
      },
      header: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing.md,
        backgroundColor: primaryColor,
        color: theme.colors.textInverse,
        flexShrink: 0
      },
      avatar: {
        fontSize: "2rem",
        marginRight: theme.spacing.sm
      },
      headerInfo: {
        flex: 1
      },
      headerName: {
        margin: 0,
        fontSize: theme.fonts.sizeMd,
        fontWeight: theme.fonts.weightSemibold
      },
      headerSubtitle: {
        margin: 0,
        fontSize: theme.fonts.sizeXs,
        opacity: 0.9
      },
      closeButton: {
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: theme.spacing.xs,
        borderRadius: theme.borderRadius.sm,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: `background-color ${theme.animations.fast}`
      },
      messagesContainer: {
        flex: 1,
        overflowY: "auto",
        padding: theme.spacing.md,
        display: "flex",
        flexDirection: "column"
      },
      messageWrapper: {
        display: "flex",
        flexDirection: "column",
        marginBottom: theme.spacing.md
      },
      userMessageRow: {
        display: "flex",
        justifyContent: "flex-end"
      },
      botMessageRow: {
        display: "flex",
        alignItems: "flex-end",
        gap: theme.spacing.sm
      },
      messageAvatar: {
        fontSize: "1.5rem",
        flexShrink: 0
      },
      messageBubble: {
        maxWidth: "85%",
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        borderRadius: theme.borderRadius.lg,
        fontSize: theme.fonts.sizeSm,
        lineHeight: 1.5,
        // Crisp rendering
        backfaceVisibility: "hidden",
        transform: "translateZ(0)"
      },
      userBubble: {
        backgroundColor: primaryColor,
        color: theme.colors.textInverse,
        borderBottomRightRadius: theme.borderRadius.sm
      },
      botBubble: {
        backgroundColor: theme.colors.surface,
        color: theme.colors.text,
        borderBottomLeftRadius: theme.borderRadius.sm
      },
      timestamp: {
        fontSize: theme.fonts.sizeXs,
        color: theme.colors.textLight,
        marginTop: "4px",
        textAlign: "right"
      },
      menuItemsContainer: {
        marginTop: theme.spacing.sm,
        width: "100%"
      },
      inputArea: {
        padding: theme.spacing.md,
        borderTop: `1px solid ${theme.colors.border}`,
        backgroundColor: theme.colors.background,
        flexShrink: 0
      },
      inputForm: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing.sm,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.full,
        padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
        border: `1px solid ${theme.colors.border}`
      },
      input: {
        flex: 1,
        border: "none",
        background: "none",
        padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
        fontSize: theme.fonts.sizeSm,
        fontFamily: theme.fonts.family,
        color: theme.colors.text,
        outline: "none"
      },
      sendButton: {
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        backgroundColor: primaryColor,
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: `opacity ${theme.animations.fast}`,
        flexShrink: 0
      },
      emptyState: {
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: theme.colors.textLight,
        fontSize: theme.fonts.sizeSm
      }
    }),
    [theme, primaryColor, position, isOpen]
  );
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit"
    });
  };
  if (!isOpen) {
    return null;
  }
  return /* @__PURE__ */ jsxs("div", { style: styles.overlay, role: "dialog", "aria-label": "Chat window", children: [
    /* @__PURE__ */ jsxs("div", { style: styles.header, children: [
      /* @__PURE__ */ jsx("span", { style: styles.avatar, children: config.avatar || "👩‍🍳" }),
      /* @__PURE__ */ jsxs("div", { style: styles.headerInfo, children: [
        /* @__PURE__ */ jsx("h3", { style: styles.headerName, children: config.name }),
        /* @__PURE__ */ jsxs("p", { style: styles.headerSubtitle, children: [
          config.venueType === "spa" ? "Wellness Concierge" : "Dining Concierge",
          " ",
          "at ",
          config.venue
        ] })
      ] }),
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: onClose,
          style: styles.closeButton,
          "aria-label": "Close chat",
          children: /* @__PURE__ */ jsx(CloseIcon, { size: 20, color: theme.colors.textInverse })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        ref: containerRef,
        style: styles.messagesContainer,
        onScroll,
        children: [
          messages.length === 0 && !isTyping && /* @__PURE__ */ jsx("div", { style: styles.emptyState, children: "Start a conversation..." }),
          messages.map((message) => /* @__PURE__ */ jsxs("div", { style: styles.messageWrapper, children: [
            message.isUser ? (
              // User message
              /* @__PURE__ */ jsx("div", { style: styles.userMessageRow, children: /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    ...styles.messageBubble,
                    ...styles.userBubble
                  },
                  children: message.text
                }
              ) })
            ) : (
              // Bot message
              /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsxs("div", { style: styles.botMessageRow, children: [
                  /* @__PURE__ */ jsx("span", { style: styles.messageAvatar, children: config.avatar || "👩‍🍳" }),
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      style: {
                        ...styles.messageBubble,
                        ...styles.botBubble
                      },
                      dangerouslySetInnerHTML: { __html: message.text }
                    }
                  )
                ] }),
                message.menuItems && message.menuItems.length > 0 && /* @__PURE__ */ jsx("div", { style: styles.menuItemsContainer, children: message.menuItems.map((item, index) => /* @__PURE__ */ jsx(
                  MenuItemCard,
                  {
                    item,
                    messageId: message.id,
                    itemIndex: index,
                    primaryColor
                  },
                  item.id || index
                )) }),
                message.suggestions && message.suggestions.length > 0 && /* @__PURE__ */ jsx(
                  SuggestionChips,
                  {
                    suggestions: message.suggestions,
                    onSelect: onSuggestionClick,
                    primaryColor
                  }
                )
              ] })
            ),
            /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  ...styles.timestamp,
                  paddingLeft: message.isUser ? 0 : "40px"
                },
                children: formatTime(message.timestamp)
              }
            )
          ] }, message.id)),
          isTyping && /* @__PURE__ */ jsx(TypingIndicator, { avatar: config.avatar })
        ]
      }
    ),
    /* @__PURE__ */ jsx("div", { style: styles.inputArea, children: /* @__PURE__ */ jsxs("form", { style: styles.inputForm, onSubmit: handleSubmit, children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          ref: inputRef,
          type: "text",
          value: inputValue,
          onChange: (e) => setInputValue(e.target.value),
          onKeyDown: handleKeyDown,
          placeholder: "Type a message...",
          style: styles.input,
          "aria-label": "Message input"
        }
      ),
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "submit",
          style: {
            ...styles.sendButton,
            opacity: inputValue.trim() ? 1 : 0.5
          },
          disabled: !inputValue.trim(),
          "aria-label": "Send message",
          children: /* @__PURE__ */ jsx(SendIcon, { size: 18, color: theme.colors.textInverse })
        }
      )
    ] }) })
  ] });
}
function WelcomePopup({
  config,
  onDismiss,
  onOpenChat,
  position = "bottom-right"
}) {
  const theme = useTheme();
  const primaryColor = config.primaryColor || theme.colors.primary;
  const styles = useMemo(
    () => ({
      container: {
        position: "fixed",
        bottom: "96px",
        [position === "bottom-right" ? "right" : "left"]: "24px",
        maxWidth: "300px",
        backgroundColor: theme.colors.background,
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.shadows.xl,
        padding: theme.spacing.lg,
        zIndex: theme.zIndex.modal,
        animation: `ck-bounce-in ${theme.animations.normal} ${theme.animations.bounce}`,
        fontFamily: theme.fonts.family
      },
      closeButton: {
        position: "absolute",
        top: "8px",
        right: "8px",
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: "4px",
        borderRadius: theme.borderRadius.sm,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: `background-color ${theme.animations.fast}`
      },
      header: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing.sm,
        marginBottom: theme.spacing.md
      },
      avatar: {
        fontSize: "2rem",
        lineHeight: 1
      },
      nameContainer: {
        flex: 1
      },
      name: {
        margin: 0,
        fontSize: theme.fonts.sizeMd,
        fontWeight: theme.fonts.weightSemibold,
        color: theme.colors.text
      },
      venue: {
        margin: 0,
        fontSize: theme.fonts.sizeSm,
        color: theme.colors.textLight
      },
      message: {
        margin: 0,
        fontSize: theme.fonts.sizeSm,
        color: theme.colors.text,
        lineHeight: 1.5,
        marginBottom: theme.spacing.md
      },
      button: {
        width: "100%",
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        backgroundColor: primaryColor,
        color: theme.colors.textInverse,
        border: "none",
        borderRadius: theme.borderRadius.md,
        fontSize: theme.fonts.sizeSm,
        fontWeight: theme.fonts.weightMedium,
        cursor: "pointer",
        transition: `opacity ${theme.animations.fast}`
      },
      pointer: {
        position: "absolute",
        bottom: "-8px",
        [position === "bottom-right" ? "right" : "left"]: "30px",
        width: 0,
        height: 0,
        borderLeft: "8px solid transparent",
        borderRight: "8px solid transparent",
        borderTop: `8px solid ${theme.colors.background}`
      }
    }),
    [theme, primaryColor, position]
  );
  const [buttonHovered, setButtonHovered] = require$$0.useState(false);
  return /* @__PURE__ */ jsxs("div", { style: styles.container, role: "dialog", "aria-label": "Welcome message", children: [
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: onDismiss,
        style: styles.closeButton,
        "aria-label": "Dismiss",
        children: /* @__PURE__ */ jsx(CloseIcon, { size: 16, color: theme.colors.textLight })
      }
    ),
    /* @__PURE__ */ jsxs("div", { style: styles.header, children: [
      /* @__PURE__ */ jsx("span", { style: styles.avatar, children: config.avatar || "👩‍🍳" }),
      /* @__PURE__ */ jsxs("div", { style: styles.nameContainer, children: [
        /* @__PURE__ */ jsx("h3", { style: styles.name, children: config.name }),
        /* @__PURE__ */ jsx("p", { style: styles.venue, children: config.venue })
      ] })
    ] }),
    /* @__PURE__ */ jsx("p", { style: styles.message, children: config.welcomeMessage || `Hi! I'm ${config.name}, your personal ${config.venueType === "spa" ? "wellness" : "dining"} concierge. How can I help you today?` }),
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: onOpenChat,
        onMouseEnter: () => setButtonHovered(true),
        onMouseLeave: () => setButtonHovered(false),
        style: {
          ...styles.button,
          opacity: buttonHovered ? 0.9 : 1
        },
        children: "Start Chat"
      }
    ),
    /* @__PURE__ */ jsx("div", { style: styles.pointer })
  ] });
}
function ConciergeWidgetInner({
  venueId,
  apiKey,
  primaryColor,
  accentColor,
  position = "bottom-right",
  welcomeDelay = 2500,
  onOpen,
  onClose,
  baseUrl = ""
}) {
  const initialize = useConciergeStore((s) => s.initialize);
  const isOpen = useConciergeStore((s) => s.isOpen);
  const openChat = useConciergeStore((s) => s.openChat);
  const closeChat = useConciergeStore((s) => s.closeChat);
  const config = useConciergeStore((s) => s.config);
  const messages = useConciergeStore((s) => s.messages);
  const isTyping = useConciergeStore((s) => s.isTyping);
  const sendMessage = useConciergeStore((s) => s.sendMessage);
  const sendSuggestion = useConciergeStore((s) => s.sendSuggestion);
  const isInitialized = useConciergeStore((s) => s.isInitialized);
  const {
    showPopup,
    dismissPopup,
    openChat: openFromPopup
  } = useWelcomePopup({
    delay: welcomeDelay,
    enabled: true
  });
  useEffect(() => {
    initialize(venueId, apiKey, baseUrl);
  }, [venueId, apiKey, baseUrl, initialize]);
  const handleOpen = () => {
    openChat();
    onOpen == null ? void 0 : onOpen();
  };
  const handleClose = () => {
    closeChat();
    onClose == null ? void 0 : onClose();
  };
  const handleButtonClick = () => {
    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  };
  const handleOpenFromPopup = () => {
    openFromPopup();
    onOpen == null ? void 0 : onOpen();
  };
  const mergedConfig = {
    ...config,
    ...primaryColor && { primaryColor },
    ...accentColor && { accentColor }
  };
  if (!isInitialized) {
    return null;
  }
  return /* @__PURE__ */ jsxs("div", { className: "chekmenu-concierge-root", children: [
    showPopup && !isOpen && /* @__PURE__ */ jsx(
      WelcomePopup,
      {
        config: mergedConfig,
        onDismiss: dismissPopup,
        onOpenChat: handleOpenFromPopup,
        position
      }
    ),
    /* @__PURE__ */ jsx(
      ChatWindow,
      {
        isOpen,
        onClose: handleClose,
        config: mergedConfig,
        messages,
        isTyping,
        onSendMessage: sendMessage,
        onSuggestionClick: sendSuggestion,
        position
      }
    ),
    /* @__PURE__ */ jsx(
      ChatButton,
      {
        isOpen,
        onClick: handleButtonClick,
        position,
        primaryColor: primaryColor || config.primaryColor
      }
    )
  ] });
}
function ConciergeWidget(props) {
  const config = useConciergeStore((s) => s.config);
  return /* @__PURE__ */ jsx(
    ThemeProvider,
    {
      venueType: config.venueType,
      overrides: {
        colors: {
          ...props.primaryColor && { primary: props.primaryColor },
          ...props.accentColor && { accent: props.accentColor }
        }
      },
      children: /* @__PURE__ */ jsx(ConciergeWidgetInner, { ...props })
    }
  );
}
function getTimeOfDay() {
  const hour = (/* @__PURE__ */ new Date()).getHours();
  if (hour < 11) return "morning";
  if (hour < 17) return "afternoon";
  return "evening";
}
const restaurantGreetings = {
  morning: "Looking for a perfect breakfast or something light to start your day?",
  afternoon: "Ready for lunch or exploring our signature dishes?",
  evening: "Shall we find you something special for dinner tonight?"
};
const spaGreetings = {
  morning: "Perfect time for a relaxing morning treatment or massage.",
  afternoon: "How about a refreshing spa session to recharge your day?",
  evening: "Unwind with an evening massage or soothing therapy tonight."
};
const restaurantSuggestions = {
  morning: [
    "What's good for breakfast?",
    "I'd like something light to start my day",
    "Show me coffee or juice options",
    "Any fresh pastries today?"
  ],
  afternoon: [
    "What's popular for lunch right now?",
    "I'm looking for something quick",
    "Any chef's specials today?",
    "Recommend a drink with my meal"
  ],
  evening: [
    "What's special for dinner tonight?",
    "Show me signature dishes",
    "Any wine pairings available?",
    "What's a good sharing option?"
  ]
};
const spaSuggestions = {
  morning: [
    "Recommend a morning ritual",
    "I'd like a refreshing facial",
    "What treatments help energize?",
    "Any quick 30-min options?"
  ],
  afternoon: [
    "What treatments do you recommend today?",
    "I need a stress-relief massage",
    "Show me spa packages",
    "Any specials or seasonal offers?"
  ],
  evening: [
    "Suggest a treatment to unwind",
    "I'd like a calming massage",
    "Any evening relaxation specials?",
    "Show me treatments for better sleep"
  ]
};
function useTimeGreeting(venueType) {
  return useMemo(() => {
    const timeOfDay = getTimeOfDay();
    const greetings = venueType === "spa" ? spaGreetings : restaurantGreetings;
    const suggestions = venueType === "spa" ? spaSuggestions : restaurantSuggestions;
    return {
      timeOfDay,
      greeting: greetings[timeOfDay],
      suggestions: suggestions[timeOfDay]
    };
  }, [venueType]);
}
export {
  ApiError,
  ChatButton,
  ChatIcon,
  ChatWindow,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CloseIcon,
  ConciergeWidget,
  FireIcon,
  MenuItemCard,
  SendIcon,
  StarIcon,
  SuggestionChips,
  ThemeProvider,
  TypingIndicator,
  WelcomePopup,
  WineIcon,
  createConciergeApi,
  defaultTheme,
  generateSessionId,
  getDeviceId,
  getSessionId,
  spaTheme,
  useAutoScroll,
  useCarousel,
  useConciergeStore,
  useTheme,
  useTimeGreeting,
  useWelcomePopup
};
//# sourceMappingURL=index.mjs.map
